import { CoverageType } from '@/models/insuranceCoverage'
import { type ClaimsCapabilityFilterKey, matchesClaimsCapability, type QuoteSortBy } from './FiltersSection'
import { CoiSpeed, getTotalPremium, PricingKind, type QuoteCardData, QuoteCardStage } from './quoteCardTypes'

export interface QuoteFilterState {
  sortBy: QuoteSortBy
  selectedRatings: ReadonlySet<string>
  selectedCoiSpeeds: ReadonlySet<CoiSpeed>
  selectedCoverages: ReadonlySet<string>
  financingAvailable: boolean
  selectedClaimsCapabilities: ReadonlySet<ClaimsCapabilityFilterKey>
  /** Coverage types the customer asked for — quotes covering all of them rank first under "Best". */
  requestedCoverages: ReadonlySet<string>
}

/**
 * Reading order for the carousel, left to right: prices the customer can act on, then the
 * carriers waiting on them, then the work still in flight, then the work already done —
 * with declines and failures last, so they don't crowd out the offers. See {@link stageRank}
 * for the priced cards' own order.
 */
const STAGE_ORDER: Record<QuoteCardStage, number> = {
  [QuoteCardStage.PRICED]: 0,
  [QuoteCardStage.BOUND]: 4,
  [QuoteCardStage.REQUEST_TO_BIND]: 4,
  [QuoteCardStage.PREPARING]: 5,
  [QuoteCardStage.APPLICATION_SENT]: 6,
  [QuoteCardStage.DECLINED]: 7,
  [QuoteCardStage.ERROR]: 7,
  [QuoteCardStage.UNAVAILABLE]: 7,
}

/** Behind every price, ahead of every carrier the customer is merely waiting on. */
const AWAITING_CUSTOMER_RANK = 3

/**
 * A carrier that has stopped and asked for a document is the one unpriced card the customer
 * can do something about, so it must not sit behind carriers that want nothing from them.
 */
function awaitsCustomer(quote: QuoteCardData): boolean {
  return (quote.outstandingDocuments ?? []).length > 0
}

/** Unpriced cards rank by stage, except the ones blocked on the customer. */
function unpricedRank(quote: QuoteCardData): number {
  return awaitsCustomer(quote) ? AWAITING_CUSTOMER_RANK : STAGE_ORDER[quote.stage]
}

/**
 * Within the priced cards, under "Best": what Cai quoted, then what Cai estimated (still
 * subject to underwriting), then whatever the customer uploaded themselves.
 */
function stageRank(quote: QuoteCardData): number {
  if (quote.stage !== QuoteCardStage.PRICED) return unpricedRank(quote)
  if (quote.isCustomerUploaded) return 2
  return quote.pricingKind === PricingKind.INDICATION ? 1 : 0
}

/**
 * Under "Cheapest" price is the only thing ranking the offers, so every priced card competes
 * on it — a carrier estimate and the customer's own uploaded quote included. The curated
 * quote-then-estimate-then-upload order is what "Best" is for; a list headed "Cheapest" that
 * isn't in ascending price order reads as broken.
 */
function pricedFirstRank(quote: QuoteCardData): number {
  return quote.stage === QuoteCardStage.PRICED ? 0 : unpricedRank(quote)
}

/** Priced cards compare on total premium; anything unpriced sorts to the end. */
function byTotalPremium(a: QuoteCardData, b: QuoteCardData): number {
  const priceA = a.stage === QuoteCardStage.PRICED ? getTotalPremium(a) : null
  const priceB = b.stage === QuoteCardStage.PRICED ? getTotalPremium(b) : null
  if (priceA == null && priceB == null) return 0
  if (priceA == null) return 1
  if (priceB == null) return -1
  return priceA - priceB
}

/** The offer the customer has already picked leads the list, whichever sort is on. */
function byApplied(a: QuoteCardData, b: QuoteCardData): number {
  const appliedA = a.stage === QuoteCardStage.PRICED && !!a.appliedAt
  const appliedB = b.stage === QuoteCardStage.PRICED && !!b.appliedAt
  if (appliedA === appliedB) return 0
  return appliedA ? -1 : 1
}

/** What a broker put forward leads "Best" — ranked, never filtered: every price stays comparable. */
function byBrokerRecommended(a: QuoteCardData, b: QuoteCardData): number {
  const recommendedA = a.stage === QuoteCardStage.PRICED && !!a.brokerRecommended
  const recommendedB = b.stage === QuoteCardStage.PRICED && !!b.brokerRecommended
  if (recommendedA === recommendedB) return 0
  return recommendedA ? -1 : 1
}

/**
 * A quote is only "complete" if it prices every coverage the customer asked for. A partial
 * quote can undercut a complete one on price while leaving the customer uninsured for
 * something they asked about, so completeness outranks price under "Best".
 */
function coversEveryRequested(quote: QuoteCardData, requested: ReadonlySet<string>): boolean {
  if (quote.stage !== QuoteCardStage.PRICED || requested.size === 0) return false
  const priced = new Set<string>(quote.coverages.map((coverage) => coverage.type))
  return [...requested].every((type) => QUOTED_SEPARATELY.has(type) || priced.has(type))
}

/** Quoted by hand on its own card (Fetch), never by a trucking carrier: asking for it leaves those complete. */
const QUOTED_SEPARATELY: ReadonlySet<string> = new Set([CoverageType.PET])

/**
 * Filters and sorts quote cards for the quotes page. A card missing the attribute a
 * filter targets (carriers we have no sourced rating/COI/claims data for) is excluded
 * by that filter rather than silently passing it.
 */
export function filterAndSortQuotes(quotes: QuoteCardData[], state: QuoteFilterState): QuoteCardData[] {
  const {
    sortBy,
    selectedRatings,
    selectedCoiSpeeds,
    selectedCoverages,
    financingAvailable,
    selectedClaimsCapabilities,
    requestedCoverages,
  } = state

  const filtered = quotes.filter((quote) => {
    if (selectedRatings.size > 0 && !(quote.carrierRating && selectedRatings.has(quote.carrierRating[0]))) {
      return false
    }
    if (selectedCoiSpeeds.size > 0 && !(quote.coiSpeed && selectedCoiSpeeds.has(quote.coiSpeed))) {
      return false
    }
    if (
      selectedCoverages.size > 0 &&
      !(
        quote.stage === QuoteCardStage.PRICED &&
        quote.coverages.some((coverage) => selectedCoverages.has(coverage.type))
      )
    ) {
      return false
    }
    if (financingAvailable && !(quote.stage === QuoteCardStage.PRICED && quote.financing?.isStandard)) {
      return false
    }
    if (
      selectedClaimsCapabilities.size > 0 &&
      !(
        quote.stage === QuoteCardStage.PRICED &&
        [...selectedClaimsCapabilities].every((key) => matchesClaimsCapability(quote.claimsCapabilities, key))
      )
    ) {
      return false
    }
    return true
  })

  // Sort is stable, so cards that tie keep the payload's order, which is oldest row first.
  if (sortBy === 'cheapest') {
    return [...filtered].sort(
      (a, b) => byApplied(a, b) || pricedFirstRank(a) - pricedFirstRank(b) || byTotalPremium(a, b)
    )
  }

  return [...filtered].sort((a, b) => {
    const applied = byApplied(a, b)
    if (applied !== 0) return applied
    const recommended = byBrokerRecommended(a, b)
    if (recommended !== 0) return recommended
    const stages = stageRank(a) - stageRank(b)
    if (stages !== 0) return stages
    const completeA = coversEveryRequested(a, requestedCoverages)
    const completeB = coversEveryRequested(b, requestedCoverages)
    if (completeA !== completeB) return completeA ? -1 : 1
    return byTotalPremium(a, b)
  })
}
